import MaterialToolbar from "@mui/material/Toolbar";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useCart } from "../../utils/CartHooks";
import { Typography, useMediaQuery } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { Box, Button, Icon } from "../../common/components";
import { useSelector } from "../../common/hooks/useSelector";
import { useStudio } from "../../context/Studio";
import { createStyles, palette, spacing, theme } from "../../styles/theme";
import { useThemeBreakpoints } from "../../utils/themeUtils";
import DesignMenu from "../Design/DesignMenu/DesignMenu";
import { TenantLogo, LOGO_SIZE_ENUM } from "../TenantLogo";
import ActiveUserList from "./components/ActiveUserList";
import StudioDeliveryModalButton from "./components/StudioDeliveryModalButton";
import StudioNavButton from "./components/StudioNavButton";
import StudioPriceModalButton from "./components/StudioPriceModalButton";
import StudioToolbarSkeleton from "./StudioToolbarSkeleton";
import {
  ProductLinesToColor,
  ProductLinesToIcon,
} from "../../constant/common.constant";
import { LockedDesign } from "../Design/DesignCard/components";
import { useDesign } from "../../context/Design/useDesign";
import useExternalRoute from "../../utils/useExternalRoute";
import { useProject } from "../../context/Project/useProject";

export const STUDIO_TOOLBAR_HEIGHT = "48px";

const styles = createStyles({
  toolbar: {
    minHeight: STUDIO_TOOLBAR_HEIGHT,
    maxHeight: STUDIO_TOOLBAR_HEIGHT,
    background: "white",
    boxShadow: "none",
    borderBottom: `1px solid ${palette.grey.lightGrey}`,
    display: "flex",
    justifyContent: "space-between",
    px: { xs: spacing.wrapperMobile, md: spacing.mediumDesktop },
    minWidth: "100%",
    position: "relative",
  },
});

interface Props {
  designName: string;
}

const StudioToolbar = ({ ...props }: Props) => {
  const { t } = useTranslation();
  const { design } = useDesign();
  const { refetchDesign } = useDesign();
  const { refetchProject } = useProject();
  const { TRANSLATED_EXTERNAL_ROUTE } = useExternalRoute();
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isTablet = useMediaQuery(theme.breakpoints.down("lg"));

  const { isMobile } = useThemeBreakpoints();
  const { canRenameDesign, canDeleteDesign } = useSelector(
    (state) => state.userInfo
  );

  const {
    productName,
    designId,
    projectId,
    projectName,
    designState: { sizesOption = [], images, productLine },
    teamId,
    editorConfig,
    isLoading,
  } = useStudio();

  const navigate = useNavigate();

  const { addToCart } = useCart();
  const onAddToCartClick = async () => {
    addToCart(designId, sizesOption, productName, undefined, true);
  };

  const designName = useMemo(() => {
    return props.designName || t("design.untitled_design");
  }, [props.designName, t]);

  if (isLoading) {
    return <StudioToolbarSkeleton />;
  }

  return (
    <React.Fragment>
      <MaterialToolbar disableGutters={true} style={styles.toolbar}>
        <Box
          display={"flex"}
          gap={spacing.mediumDesktop}
          alignItems={"center"}
          width={{ xs: undefined, lg: "calc(400px - 16px)" }}
          maxWidth={{ xs: undefined, lg: "calc(400px - 16px)" }}
        >
          <TenantLogo size={LOGO_SIZE_ENUM.MEDIUM} fullNameLogo={false} />
          {!isSmallMobile &&
            editorConfig?.toolbar?.breadcrumbs?.backPageRoute &&
            editorConfig?.toolbar?.breadcrumbs?.backPageLabel && (
              <Box
                display={"flex"}
                alignItems={"center"}
                gap={"2px"}
                sx={{
                  maxWidth: { xs: 250, lg: 388 },
                  minWidth: { xs: 250, lg: 0 }, // Allow shrinking below content size
                  overflow: "hidden", // Ensure content doesn't overflow
                  flex: "1 1 auto",
                }}
              >
                <Box
                  display={"flex"}
                  alignItems={"center"}
                  sx={{
                    overflow: "hidden",
                    width: "100%",
                    flex: "1",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      minWidth: "min(fit-content, 45%)",
                      maxWidth: { xs: "45%", sm: "100%", lg: "45%" },
                    }}
                  >
                    <Typography
                      variant="textSm"
                      component="a"
                      href={editorConfig.toolbar.breadcrumbs!.backPageRoute}
                      onClick={(e) => {
                        e.preventDefault();
                        navigate(
                          editorConfig.toolbar.breadcrumbs!.backPageRoute
                        );
                      }}
                      data-test="studio.backToProject"
                      color={palette.grey.nearBlack}
                      sx={{
                        cursor: "pointer",
                        textDecoration: "none",
                        ":hover": { textDecoration: "underline" },
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        width: "100%",
                      }}
                    >
                      {editorConfig?.toolbar?.breadcrumbs?.backPageLabel}
                    </Typography>
                  </Box>
                  {!isTablet && (
                    <>
                      <Typography
                        variant="textSm"
                        color={palette.grey.nearBlack}
                        sx={{ px: 0.5, flexShrink: 0 }}
                      >
                        /
                      </Typography>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          minWidth: 0,
                          maxWidth: "50%",
                          gap: "2px",
                        }}
                      >
                        {productLine && (
                          <Box
                            display={"flex"}
                            alignItems={"center"}
                            p={"4px"}
                            border={`1px solid ${palette.grey.midGrey}`}
                            borderRadius={"25px"}
                            mb={"1px"}
                          >
                            <Icon
                              icon={ProductLinesToIcon[productLine]}
                              color={ProductLinesToColor[productLine]}
                              size={10}
                              weight={200}
                            />
                          </Box>
                        )}
                        <Typography
                          variant="textSm"
                          color={palette.grey.nearBlack}
                          sx={{
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            width: "100%",
                            flex: 1,
                          }}
                        >
                          {designName}
                        </Typography>
                        <LockedDesign
                          backgroundPersist
                          designId={designId}
                          locked={!!design?.locked}
                          lockedVersion={design?.lockedVersion}
                          onUpdate={async () => {
                            await refetchDesign();
                            await refetchProject();
                          }}
                        />
                        {!editorConfig?.toolbar?.hideDesignMenu && (
                          <DesignMenu
                            designId={designId}
                            designName={designName}
                            projectId={projectId}
                            canDeleteDesign={canDeleteDesign}
                            canRenameDesign={canRenameDesign}
                            designImages={images ?? []}
                            projectName={projectName ?? ""}
                            redirectToEditorOnDuplicate={
                              editorConfig?.redirectToEditorOnDuplicate
                            }
                          />
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Box>
            )}
        </Box>

        {!editorConfig?.toolbar?.hideMiddleSection && (
          <Box
            display={"flex"}
            gap={{ xs: "4px", lg: spacing.xsDesktop }}
            sx={{
              position: { xs: "absolute", lg: "unset" },
              left: { xs: "50%", lg: "unset" },
              transform: { xs: "translateX(-50%)", lg: "unset" },
              zIndex: 1,
            }}
          >
            <StudioPriceModalButton />
            <StudioNavButton
              icon="palette"
              title={isMobile ? "" : t("studio.toolbar.workWithDesigner")}
              onClick={() =>
                window.open(
                  TRANSLATED_EXTERNAL_ROUTE.WORK_WITH_DESIGNER,
                  "_blank"
                )
              }
            />
            <StudioDeliveryModalButton />
          </Box>
        )}

        {!isMobile && (
          <Box display={"flex"} gap={spacing.ctaSpacingDesktop} minWidth={300}>
            {!editorConfig?.toolbar?.hideCurrentUsers && (
              <ActiveUserList teamId={teamId} />
            )}
            {!editorConfig?.toolbar?.hideAddToCartButton && (
              <Button
                size="small"
                aria-label="addToCart"
                onClick={onAddToCartClick}
                sx={{
                  whiteSpace: "nowrap",
                }}
              >
                {t("general.addToCart")}
              </Button>
            )}
          </Box>
        )}
        {isMobile && (
          <Box minWidth={24}>
            {!editorConfig?.toolbar?.hideAddToCartButton && (
              <Icon
                icon="add_shopping_cart"
                color={palette.grey.nearBlack}
                size={24}
                onClick={onAddToCartClick}
              />
            )}
          </Box>
        )}
      </MaterialToolbar>
    </React.Fragment>
  );
};
export default StudioToolbar;
